import { Component, EventEmitter, Inject, Input, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { SmashPlayerComponent } from '@app/shared/components/smash-player/smash-player.component';
import { Device } from '@app/shared/models/device';
import { TransferFile } from '@app/shared/models/transferFile';
import { Store } from '@ngrx/store';
import Plyr, { Options, SourceInfo } from 'plyr';
import * as fromViewer from '../../reducers/viewer.reducer';
import * as fromRoot from '@app/reducers';
import { ViewerActions } from '../../actions';
import { EnvironmentActions } from '@app/core/actions';
import { DOCUMENT } from '@angular/common';
import { Constant } from 'src/constant';

@Component({
  selector: 'app-file-card',
  templateUrl: './file-card.component.html',
  styleUrls: ['./file-card.component.scss']
})
export class FileCardComponent implements OnInit {

  @Input() file: TransferFile;
  @Input() selectedFiles: TransferFile[];
  @Input() device: Device;
  @Output() videoPlaying: EventEmitter<any> = new EventEmitter<any>();

  playerDisplayed = false;
  fileSelected = false;
  playerSource: SourceInfo;
  playerOptions: Options = {};
  previewable: boolean;
  @ViewChild('player') player: SmashPlayerComponent;

  constructor(
    private store$: Store<fromRoot.State & fromViewer.State>,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  ngOnInit(): void {
    this.previewable = this.file.previewable;
  }

  handleFileSelection(selected: boolean) {
    if (selected) {
      this.store$.dispatch(ViewerActions.AddFileToDownloadList({ file: this.file }))
    } else {
      this.store$.dispatch(ViewerActions.RemoveFileFromDownloadList({ file: this.file }))
    }
  }

  handleContentClick(file: TransferFile, event: PointerEvent) {
    // dont handle bubbled event from fullscreen interactions
    if ((event as any)?.target?.dataset?.plyr) {
      return false
    }

    if (this.device.isMobile && file.type === 'image') {
      return false;
    } else if (file.previewable && file.type === 'audio' || (this.device.isMobile && file.type === 'video')) {
      if (!this.playerDisplayed) {
        this.setupPlayer(file);
      }
    } else {
      // Refresh files if file expiry is outdated
      if (Date.parse(file.expiry) - Date.now() < 0) {
        this.store$.dispatch(ViewerActions.RefreshFileAndAssociatedFiles(({ file })));
      }
      this.store$.dispatch(ViewerActions.SelectFileToPreview({ file, offsetY: event.clientY - event.layerY }));
      this.store$.dispatch(ViewerActions.DisplayModalPreview());
      this.store$.dispatch(EnvironmentActions.StopAllPlayers());
    }
  }

  handleImageError() {
    // fallback when preview has failed
    this.previewable = false;
  }

  // Section for handling players //

  setupPlayer(file: TransferFile): void {
    if (Date.parse(file.expiry) - Date.now() < 0) {
      this.store$.dispatch(ViewerActions.RefreshFileAndAssociatedFiles(({ file })));
    }
    this.playerSource = this.buildPlayerSource(file);
    this.playerOptions = this.buildPlayerOptions(file);
    this.playerDisplayed = true;
  }

  /* Get status of player for audio and video */
  get playerIsPlaying(): boolean {
    if (!!this.player) {
      return !!this.player.isPlaying;
    }
    return false;
  }

  killPlayer(event: Event) {
    if (this.player) {
      this.player.destroy();
      this.playerDisplayed = false;
      event.stopPropagation();
      this.videoPlaying.emit(false);
      this.renderer.removeClass(this.document.body, 'video-playing');
    }
  }

  togglePlay(event: Event, file?: TransferFile): void {
    if (file && !this.playerDisplayed) {
      this.setupPlayer(file);
    }

    if (this.player) {
      if (this.file.type === 'audio') {
        if (this.player.isPlaying) {
          this.player.pause();
          this.playerDisplayed = false;
        } else {
          this.player.play();
          this.playerDisplayed = true;
        }
        event.stopPropagation();
        event.preventDefault();
      }

      if (this.file.type === 'video') {
        if (!this.player.isPlaying) {
          this.player.play();
          this.videoPlaying.emit(true);
          this.renderer.addClass(this.document.body, 'video-playing');
        }
      }
    }
  }

  buildPlayerSource(item: TransferFile): SourceInfo {
    const sources: SourceInfo = {
      sources: [
        {
          src: item.preview,
          provider: Constant.videoProviders[item.provider],
        }
      ],
      type: item.type as Plyr.MediaType,
    }
    return sources;
  }

  buildPlayerOptions(item: TransferFile): Options {
    const options: Options = {
      volume: 100,
      disableContextMenu: true,
      storage: { enabled: false }
    };

    if (item.type === 'audio') {
      options.controls = ['play', 'progress', 'current-time', 'invert-time'];
      options.autoplay = true;
    }

    if (item.type === 'video') {
      options.hideControls = false;
      options.clickToPlay = false;
      options.autoplay = true;
      options.fullscreen = { iosNative: true, enabled: true, fallback: true };
      options.controls = ['play', 'progress', 'current-time', 'invert-time', 'fullscreen'];
    }
    return options;
  }

}
